import React, {useEffect} from 'react'
import * as PropTypes from 'prop-types'
import {Col, H3, Row, WarningAlert} from '@modifi/ui'
import {DetailsTable} from '../../../../styles/common'
import useStickyHeader from '../../../../util/hooks/useStickyHeader'

import {Header, StyledHighlight, Wrapper} from './styles'
import {matchGroups, sourceTypes} from '../../constants'
import {formMap, boxMap} from '../../config'
import {TransactionBox} from '../../../transactions'
import {AmountDetails} from '../../../shared/components/amount-details'
import FooterSubmitButton from '../footer-submit-button'
import {getMatchType, matchTypeToLabel} from '../../../shared/utils'
import {useAbility} from '../../../../constants/permissions'
import {TRANSACTION} from '../../../../constants/section'

const ConfirmMatchDialog = ({
  params: {matchGroup, sourceEntity, targetEntities},
  pending,
  matchCompleted,
  confirmMatch,
  closeDialog,
  cleanupMatch,
}) => {
  const {tableRef, isSticky} = useStickyHeader()

  useEffect(() => () => cleanupMatch(), [cleanupMatch])

  const ability = useAbility()
  const isAllowed = ability.can('match', TRANSACTION)

  const matchType = getMatchType(matchGroup, sourceEntity)

  const TargetBox = boxMap[matchGroup]

  const CustomForm = formMap[matchType]

  const handleConfirm = () => {
    confirmMatch({
      matchGroup,
      sourceEntity,
      matchType,
      targetEntities,
    })
  }

  return (
    <Wrapper>
      {!isAllowed && (
        <Row mt={30} mb={10}>
          <Col col={4} offset={4}>
            <WarningAlert>
              You are lacking permissions to adjust values in this section
            </WarningAlert>
          </Col>
        </Row>
      )}
      <Header>
        <H3>Match Transaction</H3>
      </Header>
      <Row>
        <Col col={5}>
          <StyledHighlight>Classified as</StyledHighlight>
        </Col>
      </Row>

      <Row alignItems="center">
        <Col col={5}>{matchTypeToLabel(matchType)}</Col>
      </Row>

      <DetailsTable ref={tableRef}>
        <TransactionBox
          {...sourceEntity}
          matchType={matchType}
          matchGroup={matchGroup}
          isSticky={isSticky}
          inverseBackground
        />
        <TargetBox
          targetEntities={targetEntities}
          matchType={matchType}
          sourceEntity={sourceEntity}
          inverseBackground
        />
        {sourceEntity?.actions?.canSplitTransaction && (
          <AmountDetails
            amount={sourceEntity?.amount}
            currency={sourceEntity?.currency}
            matchGroup={matchGroup}
            splitAmounts={targetEntities.map(({inputAmount}) => inputAmount)}
            isBox
          />
        )}
      </DetailsTable>

      {CustomForm ? (
        <CustomForm
          sourceEntity={sourceEntity}
          targetEntities={targetEntities}
          matchType={matchType}
          matchGroup={matchGroup}
          confirmMatch={confirmMatch}
          pending={pending}
          completed={matchCompleted}
          closeDialog={closeDialog}
        />
      ) : (
        <Row justifyContent="center" mt={40}>
          <Col auto>
            <FooterSubmitButton
              pending={pending}
              completed={matchCompleted}
              closeDialog={closeDialog}
              onClick={handleConfirm}
            />
          </Col>
        </Row>
      )}
    </Wrapper>
  )
}

ConfirmMatchDialog.propTypes = {
  params: PropTypes.shape({
    matchGroup: PropTypes.oneOf(matchGroups),
    sourceType: PropTypes.oneOf(sourceTypes).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    sourceEntity: PropTypes.object.isRequired,
    targetEntities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        inputAmount: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,

  confirmMatch: PropTypes.func.isRequired,
  cleanupMatch: PropTypes.func.isRequired,
  pending: PropTypes.bool.isRequired,
  matchCompleted: PropTypes.bool.isRequired,

  closeDialog: PropTypes.func.isRequired,
}

export default ConfirmMatchDialog
